import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query'
import type {Dispatch} from '@reduxjs/toolkit'
import {isNewAuthSystemEnabled} from './auth.ts'
import {fetchBaseQuery} from '@reduxjs/toolkit/query/react'
import {getToken} from '@app/util-functions'
import type {ExtraOptions} from '@app/shared/interfaces/common/api'
import {logoutUserAction} from '@redux/slices/authSlice'
import {isRunningInTeams} from '@shared/utils/teamsRuntime'
import {revokeProviderTokens} from '@shared/utils/revokeTokens'
import {
  DISPLAY_UNAUTHORIZED_EVENT,
  isDashboardPrintPath,
  isDisplayPath,
  PRINT_UNAUTHORIZED_EVENT,
} from '@shared/utils/printRoute'
import {
  clearDisplayCredential,
  getDisplayAccessToken,
  readDisplayCredential,
  refreshDisplayCredential,
} from '@shared/display/displayCredential'
import {buildAuthorizeUrl, requestForceLogin} from '@shared/utils/buildAuthorizeUrl'
import {clearTokenExpiry, readTokenExpiresAt} from '@shared/utils/tokenExpiry'
import {runPrintRead} from './printRouteRequests'

/**
 * Fired instead of the hard redirect on a 401 inside the Teams tab —
 * `window.location.href = '/'` lands on the login page in a browser, but on a
 * blank frame in Teams (auth.montblanc.ai refuses to be framed).
 * `src/teams/TeamsApp.tsx` listens for this and runs the silent Teams SSO
 * again, or shows the sign-in screen.
 */
export const TEAMS_REAUTH_REQUIRED_EVENT = 'mbai:teams-reauth-required'

/**
 * `detail` of TEAMS_REAUTH_REQUIRED_EVENT. `explicitLogout` marks the user's
 * own logout, after which TeamsApp shows the sign-in screen instead of
 * signing straight back in (AB#6617).
 */
export interface TeamsReauthRequiredDetail {
  explicitLogout?: boolean
}

/**
 * sessionStorage timestamp (ms) of TeamsApp's last automatic silent sign-in
 * after TEAMS_REAUTH_REQUIRED_EVENT. Another event within
 * TEAMS_AUTO_REAUTH_GUARD_MS shows the sign-in screen instead, so a server
 * that keeps refusing the fresh token doesn't loop (AB#6617).
 */
export const TEAMS_AUTO_REAUTH_GUARD_KEY = 'mbai-teams-auto-reauth-at'
export const TEAMS_AUTO_REAUTH_GUARD_MS = 60_000

type apiPath = 'core' | 'edge' | 'aicore' | 'ad' | 'auth' | 'tenants' | 'organizations'

type MontBlancBaseQuery = BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  Record<string, unknown>,
  FetchBaseQueryMeta
>

/**
 * Type predicate to narrow an unknown error to `FetchBaseQueryError`
 */
export const isFetchBaseQueryError = (error: unknown): error is FetchBaseQueryError =>
  typeof error === 'object' && error != null && 'status' in error

/**
 * Type predicate to narrow an unknown error to an object with a string 'message' property
 */
export const isErrorWithMessage = (error: unknown): error is {message: string} =>
  typeof error === 'object' &&
  error != null &&
  'message' in error &&
  typeof error.message === 'string'

/**
 * Sets the MontBlanc auth headers on `headers`. Shared by the RTK base query and
 * by raw `fetch` calls that cannot go through RTK (e.g. keepalive sends on pagehide).
 */
export const setAuthHeaders = (headers: Headers): Headers => {
  const token = getToken()
  if (!token) return headers

  if (isNewAuthSystemEnabled) {
    headers.set('Authorization', `Bearer ${token}`)
    const username = localStorage.getItem('username')
    if (username) {
      headers.set('X-Auth-Username', `MbaiUser ${username}`)
    }
  } else {
    headers.set('Authorization', `Token ${token}`)
  }
  return headers
}

const buildBaseQuery = (baseUrl: string) =>
  fetchBaseQuery({
    baseUrl,
    prepareHeaders: setAuthHeaders,
  })

type UnauthorizedHandlingOptions = {
  authContext?: ExtraOptions['authContext'] | null
  // Optional so callers outside RTK (e.g. the WebSocket ticket fetch in
  // shared/websocket/serviceSocket.ts) can share this path without a store.
  dispatch?: Dispatch
  // The access token the failed request was sent with (`getToken()` right
  // before sending). When localStorage holds a different one by now, another
  // request or tab already rotated it and the request is simply retried.
  // `undefined` skips that check.
  requestToken?: string | null
}

/**
 * sessionStorage timestamp (ms) of the last silent re-authorize. A second one
 * within SILENT_REAUTH_GUARD_MS means the fresh tokens are rejected too, so
 * the user is logged out for real instead of redirected in a loop.
 */
export const SILENT_REAUTH_GUARD_KEY = 'mbai-silent-reauth-at'
export const SILENT_REAUTH_GUARD_MS = 60_000

// Set once this page started leaving (silent re-authorize or logout), so the
// other requests that 401 at the same moment don't start a second exit -- the
// second one would find the guard set and log the user out for real.
let leavingPage = false
// Removes the pending "navigation was cancelled" listeners and fallback timer
// of the proactive redirect (armProactiveRecovery), if any.
let disarmProactiveRecovery: (() => void) | undefined

/** Test hook: forget that this page started leaving. */
export const resetUnauthorizedHandling = (): void => {
  disarmProactiveRecovery?.()
  leavingPage = false
}

const dispatchLogout = (dispatch: Dispatch | undefined): void => {
  if (dispatch) {
    dispatch(logoutUserAction())
    return
  }
  // logoutUserAction's only effect outside the store is dropping the token;
  // do the same without a store so the reload below lands logged out.
  localStorage.removeItem('token')
  clearTokenExpiry()
}

// Today's logout: revoke on the auth provider, clear storage, reload on '/'.
const logOutAndReload = async (
  authContext: UnauthorizedHandlingOptions['authContext'],
  dispatch: Dispatch | undefined,
): Promise<false> => {
  // Start the revocation before logoutUser() so both share one request
  // (revokeProviderTokens is single-flight and snapshots the tokens), and
  // wait for it (bounded) before the hard redirect below, which would
  // otherwise abort it mid-flight (AB#6319).
  const revocation = revokeProviderTokens()
  authContext?.logoutUser?.()
  await revocation
  dispatchLogout(dispatch)
  window.location.href = '/'
  return false
}

/**
 * The dashboard print route (AB#6645) runs on a read-only session: a write
 * would be refused, and the usual 401 handling would log the renderer out and
 * redirect, leaving it to print the login page. So there, mutations are never
 * sent, and a 401 is reported to the page instead of logging out.
 */
const runPrintRouteQuery = async (
  baseUrl: string,
  args: Parameters<MontBlancBaseQuery>[0],
  api: Parameters<MontBlancBaseQuery>[1],
  extraOptions: ExtraOptions,
) => {
  if (api.type === 'mutation') {
    const error: FetchBaseQueryError = {status: 'CUSTOM_ERROR', error: 'Read-only while printing'}
    return {error}
  }
  // Queued, retried once on a transient failure, recorded if it still fails
  // (AB#6933, see printRouteRequests).
  const result = await runPrintRead(printReadKey(args), async () =>
    buildBaseQuery(baseUrl)(args, api, extraOptions),
  )
  if (result.error && 'status' in result.error && result.error.status === 401) {
    window.dispatchEvent(new CustomEvent(PRINT_UNAUTHORIZED_EVENT))
  }
  return result
}

const printReadKey = (args: string | FetchArgs): string => {
  if (typeof args === 'string') return `GET ${args}`
  const params = args.params
    ? `?${new URLSearchParams(args.params as Record<string, string>).toString()}`
    : ''
  return `${args.method ?? 'GET'} ${args.url}${params}`
}

/**
 * The shop-floor display routes (AB#6756) run on the screen's own credential,
 * never the `token` of a person who may also be signed in on this browser. The
 * screen may only read: mutations are never sent (the server refuses them too).
 * A 401 is retried once after a refresh; if the screen has been revoked it is
 * reported to the page, which goes back to pairing, instead of logging out.
 */
const runDisplayRouteQuery = async (
  baseUrl: string,
  args: Parameters<MontBlancBaseQuery>[0],
  api: Parameters<MontBlancBaseQuery>[1],
  extraOptions: ExtraOptions,
) => {
  if (api.type === 'mutation') {
    const error: FetchBaseQueryError = {status: 'CUSTOM_ERROR', error: 'Read-only on a display'}
    return {error}
  }
  const query = (token: string | null) =>
    fetchBaseQuery({
      baseUrl,
      prepareHeaders: (headers) => {
        if (token) headers.set('Authorization', `Bearer ${token}`)
        return headers
      },
    })(args, api, extraOptions)

  let result = await query(await getDisplayAccessToken())
  if (result.error && 'status' in result.error && result.error.status === 401) {
    const refreshed = await refreshDisplayCredential()
    if (refreshed) {
      result = await query(refreshed.accessToken)
    }
    // A refresh that failed on the network keeps the credential and the next
    // tick tries again; a refused refresh, or a refused fresh token, is a revoke.
    const revoked = refreshed
      ? !!result.error && 'status' in result.error && result.error.status === 401
      : readDisplayCredential() === null
    if (revoked) {
      clearDisplayCredential()
      window.dispatchEvent(new CustomEvent(DISPLAY_UNAUTHORIZED_EVENT))
    }
  }
  return result
}

/**
 * Whether the failed request went out with a token that is no longer the
 * stored one: another request, or another tab's re-authorize, stored a fresh
 * token meanwhile, and the request is simply retried with it.
 */
const tokenRotatedSince = (requestToken: string | null | undefined): boolean => {
  const storedToken = localStorage.getItem('token')
  return requestToken !== undefined && !!storedToken && storedToken !== requestToken
}

/**
 * The 401 path inside the Teams tab (AB#6617): drop the dead token and let
 * TeamsApp run the silent Teams SSO again (guarded there, at most once a
 * minute). Deliberately no revoke: the auth provider's revoke ends every
 * auth-site session of the user, in every browser, so revoking on each daily
 * token expiry signed the user out of all their browser tabs too. No reload
 * either: the Teams frame would go blank. Not authContext.logoutUser():
 * LegacyAuthProvider's logoutUser() revokes, and is the user's own explicit
 * sign-out.
 */
const requestTeamsReauth = (dispatch: Dispatch | undefined): false => {
  localStorage.removeItem('token')
  // Leftover from before refresh tokens were dropped (AB#6617).
  localStorage.removeItem('refresh_token')
  localStorage.removeItem('expires_in')
  localStorage.removeItem('username')
  clearTokenExpiry()
  dispatchLogout(dispatch)
  window.dispatchEvent(new CustomEvent(TEAMS_REAUTH_REQUIRED_EVENT))
  return false
}

const silentReauthRecentlyTried = (): boolean => {
  const at = Number.parseInt(sessionStorage.getItem(SILENT_REAUTH_GUARD_KEY) ?? '', 10)
  return Number.isFinite(at) && Date.now() - at < SILENT_REAUTH_GUARD_MS
}

/**
 * Full-page round trip through the auth site's authorize endpoint. While the
 * auth site's session is valid it hands a code straight back and
 * AuthCodeCallback returns to the current page with fresh tokens; without a
 * session it shows the login form. Deliberately no revoke: the auth provider's
 * revoke ends every auth-site session of the user, including the one this
 * round trip relies on.
 */
const reauthorizeSilently = async (
  authContext: UnauthorizedHandlingOptions['authContext'],
  dispatch: Dispatch | undefined,
): Promise<false> => {
  sessionStorage.setItem(SILENT_REAUTH_GUARD_KEY, Date.now().toString())
  localStorage.removeItem('token')
  // Leftover from before refresh tokens were dropped (AB#6617).
  localStorage.removeItem('refresh_token')
  localStorage.removeItem('expires_in')
  clearTokenExpiry()
  dispatchLogout(dispatch)

  const {pathname, search, hash} = window.location
  let authorizeUrl: string
  try {
    authorizeUrl = await buildAuthorizeUrl(`${pathname}${search}${hash}`)
  } catch (error) {
    console.error('Failed to build the authorize URL:', error)
    return logOutAndReload(authContext, dispatch)
  }
  window.location.assign(authorizeUrl)
  return false
}

/**
 * The shared 401 path for the RTK base query and the WebSocket ticket fetch.
 * Resolves `true` when the caller should retry its request once (another
 * request or tab stored a fresh token meanwhile); `false` once the page is
 * leaving for a re-authorize or the user was logged out. The UI app gets no
 * refresh tokens (AB#6615), so there is no refresh step.
 *
 * In the Teams tab: requestTeamsReauth. In a browser tab (AB#6617):
 * re-authorize silently against the auth site's session, else (that failed
 * less than a minute ago) a real logout ending on the login form.
 */
export const handleUnauthorized = async ({
  authContext,
  dispatch,
  requestToken,
}: UnauthorizedHandlingOptions): Promise<boolean> => {
  if (isRunningInTeams()) {
    // A request that went out before TeamsApp's sign-in stored a fresh
    // token must not drop that token.
    if (tokenRotatedSince(requestToken)) {
      return true
    }
    return requestTeamsReauth(dispatch)
  }

  // The legacy auth system (no authorize endpoint): unchanged, straight to
  // logout.
  if (!isNewAuthSystemEnabled) {
    return logOutAndReload(authContext, dispatch)
  }

  if (leavingPage) {
    return false
  }

  if (tokenRotatedSince(requestToken)) {
    return true
  }

  leavingPage = true

  if (silentReauthRecentlyTried()) {
    // The tokens from the last silent re-authorize were rejected too: a real
    // logout, and the next authorize must show the form (AB#6617).
    requestForceLogin()
    return logOutAndReload(authContext, dispatch)
  }

  return reauthorizeSilently(authContext, dispatch)
}

/**
 * Re-authorize ahead of time once the access token has less than this left,
 * or less than half its lifetime, whichever is shorter (see
 * proactiveReauthWindowMs).
 */
export const PROACTIVE_REAUTH_WINDOW_MS = 60 * 60_000

/**
 * Fallback for armProactiveRecovery: a page that is still alive this long
 * after the proactive redirect started, with no interaction meanwhile, did
 * not leave either. The page becomes usable again (AB#6617).
 */
export const PROACTIVE_REAUTH_CANCELLED_AFTER_MS = 30_000

// The routes that are part of signing in, plus the public pages that must
// never redirect: the code callback is storing the fresh token right now,
// /login is on its way to the auth site anyway, /recorder is Birdie's blank
// landing page and /alpinist/sharepoint-callback the Entra consent popup.
const PROACTIVE_REAUTH_EXCLUDED_ROUTES = [
  '/authorize-callback',
  '/authentication/code/callback',
  '/login',
  '/recorder',
  '/alpinist/sharepoint-callback',
]

const isExcludedRoute = (pathname: string): boolean => {
  const path = pathname.replace(/\/+$/, '') || '/'
  return PROACTIVE_REAUTH_EXCLUDED_ROUTES.includes(path)
}

/**
 * The proactive window for the stored token: PROACTIVE_REAUTH_WINDOW_MS, but
 * at most half the token's lifetime (the `expires_in` stored at sign-in, in
 * seconds), so a short-lived token isn't renewed right after sign-in. Without
 * a stored `expires_in`, PROACTIVE_REAUTH_WINDOW_MS.
 */
export const proactiveReauthWindowMs = (): number => {
  const lifetimeSeconds = Number(localStorage.getItem('expires_in'))
  if (!Number.isFinite(lifetimeSeconds) || lifetimeSeconds <= 0) {
    return PROACTIVE_REAUTH_WINDOW_MS
  }
  return Math.min(PROACTIVE_REAUTH_WINDOW_MS, (lifetimeSeconds * 1000) / 2)
}

/**
 * After the proactive redirect started: the first `pointerdown` or `keydown`,
 * or a back/forward-cache restore (`pageshow` with `persisted`), means the
 * navigation was cancelled -- a user who answered a `beforeunload` "Leave
 * site?" with Stay works on the page, a user waiting for a slow first hop
 * doesn't. This page may act again then, and since no round trip happened, a
 * later 401 must not mistake it for a failed one: the guard goes too, if it
 * is still this attempt's. PROACTIVE_REAUTH_CANCELLED_AFTER_MS without any
 * of these does the same.
 */
const armProactiveRecovery = (guardAt: string): void => {
  disarmProactiveRecovery?.()

  const recover = (): void => {
    disarm()
    leavingPage = false
    if (sessionStorage.getItem(SILENT_REAUTH_GUARD_KEY) === guardAt) {
      sessionStorage.removeItem(SILENT_REAUTH_GUARD_KEY)
    }
  }
  const onPageShow = (event: PageTransitionEvent): void => {
    if (event.persisted) {
      recover()
    }
  }
  const timer = setTimeout(recover, PROACTIVE_REAUTH_CANCELLED_AFTER_MS)
  const disarm = (): void => {
    clearTimeout(timer)
    window.removeEventListener('pointerdown', recover, true)
    window.removeEventListener('keydown', recover, true)
    window.removeEventListener('pageshow', onPageShow)
    if (disarmProactiveRecovery === disarm) {
      disarmProactiveRecovery = undefined
    }
  }

  // Capture phase, so a handler that stops propagation can't hide the
  // interaction.
  window.addEventListener('pointerdown', recover, true)
  window.addEventListener('keydown', recover, true)
  window.addEventListener('pageshow', onPageShow)
  disarmProactiveRecovery = disarm
}

/**
 * The proactive round trip through the auth site's authorize endpoint. Unlike
 * reauthorizeSilently (the 401 path, whose token is dead) it keeps the stored
 * tokens and dispatches no logout: the token is still valid, the callback
 * overwrites it on return, other tabs keep using it meanwhile, and the page
 * stays usable when the navigation is cancelled. Resolves `true` once the
 * navigation started.
 */
const reauthorizeProactively = async (): Promise<boolean> => {
  const guardAt = Date.now().toString()
  sessionStorage.setItem(SILENT_REAUTH_GUARD_KEY, guardAt)

  const {pathname, search, hash} = window.location
  let authorizeUrl: string
  try {
    authorizeUrl = await buildAuthorizeUrl(`${pathname}${search}${hash}`)
  } catch (error) {
    // The token is still valid: stay, and let the 401 path handle its expiry.
    console.error('Failed to build the authorize URL:', error)
    sessionStorage.removeItem(SILENT_REAUTH_GUARD_KEY)
    leavingPage = false
    return false
  }

  window.location.assign(authorizeUrl)
  armProactiveRecovery(guardAt)
  return true
}

type ProactiveReauthorizeOptions = {
  // Whether the page holds unsaved edits (GlobalUnsavedChangesGuard's
  // condition). Read at check time; while true, nothing redirects.
  hasUnsavedChanges?: () => boolean
}

/**
 * Proactive re-authorize for a stale tab, on load and on the next in-app
 * navigation (AB#6617, AB#6757): when the stored access token expires within
 * proactiveReauthWindowMs(), or already has, go through the auth site now,
 * instead of on the first failing request. The valid token stays in place
 * meanwhile (reauthorizeProactively).
 *
 * Does nothing in the Teams tab (no top-level redirect there), with the
 * legacy auth system, without a token, on a sign-in or public route, without
 * a known `token_expires_at`, while there are unsaved changes, while this
 * page is already leaving, or when a silent re-authorize ran less than
 * SILENT_REAUTH_GUARD_MS ago (the 401 path decides what happens then).
 * Resolves `true` when the page is leaving for the auth site.
 */
export const reauthorizeIfTokenExpiring = async ({
  hasUnsavedChanges,
}: ProactiveReauthorizeOptions = {}): Promise<boolean> => {
  if (isRunningInTeams() || !isNewAuthSystemEnabled || leavingPage) {
    return false
  }
  if (!localStorage.getItem('token') || isExcludedRoute(window.location.pathname)) {
    return false
  }
  const expiresAt = readTokenExpiresAt()
  if (expiresAt === null || expiresAt - Date.now() >= proactiveReauthWindowMs()) {
    return false
  }
  if (silentReauthRecentlyTried() || hasUnsavedChanges?.()) {
    return false
  }
  leavingPage = true
  return reauthorizeProactively()
}

const runWithAuthHandling = async (
  baseUrl: string,
  args: Parameters<MontBlancBaseQuery>[0],
  api: Parameters<MontBlancBaseQuery>[1],
  extraOptions: ExtraOptions,
) => {
  if (isDashboardPrintPath()) {
    return runPrintRouteQuery(baseUrl, args, api, extraOptions)
  }
  if (isDisplayPath()) {
    return runDisplayRouteQuery(baseUrl, args, api, extraOptions)
  }
  // The token this request goes out with (prepareHeaders reads the same),
  // for the "another request already rotated it" check on a 401.
  const requestToken = getToken()
  const result = await buildBaseQuery(baseUrl)(args, api, extraOptions)

  // Handle 401 Unauthorized with proper type guard
  if (result.error && 'status' in result.error && result.error.status === 401) {
    const shouldRetry = await handleUnauthorized({
      authContext: extraOptions.authContext,
      dispatch: api.dispatch,
      requestToken,
    })
    if (shouldRetry) {
      return buildBaseQuery(baseUrl)(args, api, extraOptions)
    }
  }

  return result
}

export const createMontBlancBaseQuery = (apiPath: apiPath): MontBlancBaseQuery => {
  return async (args, api, extraOptions: ExtraOptions = {}) => {
    const baseUrl = import.meta.env.VITE_APP_API_BASE_URL

    if (!baseUrl) {
      console.warn(`VITE_APP_API_BASE_URL is not defined for ${apiPath} API`)
    }

    return runWithAuthHandling(`${baseUrl || ''}/${apiPath}/api/`, args, api, extraOptions)
  }
}

/**
 * Anomaly detection lives in mbai-pm-core (AB#5658) and is reached through the
 * gateway's `/core/`-routed path, not `/{service}/` like the other APIs — hence
 * a dedicated base query instead of createMontBlancBaseQuery.
 */
export const createAnomalyDetectionBaseQuery = (): MontBlancBaseQuery => {
  return async (args, api, extraOptions: ExtraOptions = {}) => {
    const baseUrl = import.meta.env.VITE_APP_API_BASE_URL

    if (!baseUrl) {
      console.warn('VITE_APP_API_BASE_URL is not defined for anomaly detection API')
    }

    return runWithAuthHandling(`${baseUrl || ''}/core/api/ad/`, args, api, extraOptions)
  }
}
